import type * as LogRocket from 'logrocket';

/**
 * What LogRocket may record from a session. See
 * openspec/changes/logrocket-redaction (capability
 * `session-recording-redaction`).
 *
 * Network bodies are an allow-list (first-party `/api/` calls only), headers
 * and URL parameters are deny-lists. Anything unknown fails closed.
 */

type LogRocketOptions = NonNullable<Parameters<typeof LogRocket.init>[1]>;
type RequestSanitizer = NonNullable<
  NonNullable<LogRocketOptions['network']>['requestSanitizer']
>;
type ResponseSanitizer = NonNullable<
  NonNullable<LogRocketOptions['network']>['responseSanitizer']
>;
type LogRocketRequest = Parameters<RequestSanitizer>[0];
type LogRocketResponse = Parameters<ResponseSanitizer>[0];

/** Headers whose values never reach a recording (compared lowercase). */
const SENSITIVE_HEADERS = new Set([
  'authorization',
  'cookie',
  'set-cookie',
  'x-firebase-appcheck',
  'x-goog-api-key',
  'x-firebase-gmpid',
]);

/** Query parameters whose values never reach a recording. */
const SENSITIVE_PARAMS = [
  'code',
  'oobCode',
  'apiKey',
  'key',
  'access_token',
  'id_token',
  'refresh_token',
  'token',
  '$httpHeaders',
];

/**
 * First-party RPC services whose messages carry provider credentials, so their
 * bodies are never recorded even though they are `/api/` calls.
 * SettingsService: Dropbox `app_secret`, `access_token`, `refresh_token`
 * (proto/v1/settings.proto). If a second service needs this, move the marker
 * into the proto as a field option instead of extending this list.
 */
const BODY_DENY_SERVICES = ['SettingsService'];

/** Cap on remembered request ids; the oldest is evicted first. */
const MAX_TRACKED_REQUESTS = 500;

/**
 * Removes credential query parameters from a URL. Returns `null` if the URL
 * cannot be parsed, so a recording fails closed rather than keeping the raw
 * string.
 */
export function sanitizeUrl(
  url: string,
  base: string | undefined = currentOrigin(),
): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url, base);
  } catch {
    return null;
  }
  for (const param of SENSITIVE_PARAMS) {
    parsed.searchParams.delete(param);
  }
  // A relative input stays relative; `new URL` only needed the base to parse.
  return isAbsolute(url)
    ? parsed.toString()
    : `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

function isAbsolute(url: string): boolean {
  return /^[a-z][a-z0-9+.-]*:/i.test(url);
}

function currentOrigin(): string | undefined {
  return typeof location === 'undefined' ? undefined : location.origin;
}

function stripHeaders<T extends { [key: string]: string | null | undefined }>(
  headers: T | undefined,
): { [key: string]: string | null | undefined } {
  const result: { [key: string]: string | null | undefined } = {};
  for (const [name, value] of Object.entries(headers ?? {})) {
    if (!SENSITIVE_HEADERS.has(name.toLowerCase())) {
      result[name] = value;
    }
  }
  return result;
}

/** Whether a call's bodies may be recorded: own `/api/`, not a deny-listed RPC service. */
export function isBodyRecordable(
  url: string,
  origin: string | undefined = currentOrigin(),
): boolean {
  if (!origin) return false;
  let parsed: URL;
  try {
    parsed = new URL(url, origin);
  } catch {
    return false;
  }
  if (parsed.origin !== origin) return false;
  if (!parsed.pathname.startsWith('/api/')) return false;
  // ConnectRPC paths are /api/<package>.<Service>/<Method>.
  const service = parsed.pathname.slice('/api/'.length).split('/')[0] ?? '';
  const name = service.split('.').pop();
  return !BODY_DENY_SERVICES.includes(name ?? '');
}

/**
 * Builds the request and response sanitizers. Both are closed over one
 * request-id map: responses are matched to their request by `reqId` (a
 * response's `url` is optional), and an unknown id defaults to "no body".
 */
export function createNetworkSanitizers(
  origin: string | undefined = currentOrigin(),
): {
  requestSanitizer: RequestSanitizer;
  responseSanitizer: ResponseSanitizer;
} {
  const allowBody = new Map<string, boolean>();

  const requestSanitizer: RequestSanitizer = (request: LogRocketRequest) => {
    const recordable = isBodyRecordable(request.url, origin);
    allowBody.set(request.reqId, recordable);
    if (allowBody.size > MAX_TRACKED_REQUESTS) {
      const oldest = allowBody.keys().next();
      if (!oldest.done) allowBody.delete(oldest.value);
    }
    return {
      ...request,
      url: sanitizeUrl(request.url, origin) as string | null,
      headers: stripHeaders(request.headers),
      body: recordable ? request.body : null,
    };
  };

  const responseSanitizer: ResponseSanitizer = (
    response: LogRocketResponse,
  ) => {
    const recordable = allowBody.get(response.reqId) ?? false;
    allowBody.delete(response.reqId);
    return {
      ...response,
      headers: stripHeaders(response.headers),
      body: recordable ? response.body : null,
    };
  };

  return { requestSanitizer, responseSanitizer };
}

/** Matches an encoded JWT (header.payload.signature, base64url, `eyJ` prefix). */
const JWT_PATTERN = /^eyJ[\w-]+\.[\w-]+\.[\w-]*$/;
const TOKEN_KEYS = new Set(['token', 'idToken', 'refreshToken', 'accessToken']);
const REDACTED = '[redacted]';
const MAX_DEPTH = 4;

/**
 * Returns `value` with every encoded-token property removed and every
 * JWT-shaped string replaced, or the same reference if nothing needed to
 * change. Only string values under token-named keys are dropped, so the
 * claims object in `auth.token` survives.
 */
export function redactTokens<T>(value: T, depth = 0): T {
  if (typeof value === 'string') {
    return (JWT_PATTERN.test(value) ? REDACTED : value) as T;
  }
  if (value === null || typeof value !== 'object' || depth > MAX_DEPTH) {
    return value;
  }
  if (Array.isArray(value)) {
    let changed = false;
    const mapped = value.map((item) => {
      const next = redactTokens(item, depth + 1);
      if (next !== item) changed = true;
      return next;
    });
    return (changed ? mapped : value) as T;
  }
  let changed = false;
  const result: Record<string, unknown> = {};
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    if (TOKEN_KEYS.has(key) && typeof item === 'string') {
      changed = true;
      continue;
    }
    const next = redactTokens(item, depth + 1);
    if (next !== item) changed = true;
    result[key] = next;
  }
  return (changed ? result : value) as T;
}

export interface NgrxSanitizerOptions {
  /** App-specific action trimming, applied before token redaction. */
  actionSanitizer?: (action: any) => any;
}

/**
 * `actionSanitizer` / `stateSanitizer` for `createNgrxMiddleware`. The
 * store holds claims only; this is the backstop for a state rehydrated from
 * `localStorage` in the old shape, and for any future token-shaped value.
 */
export function createNgrxSanitizers(options: NgrxSanitizerOptions = {}): {
  actionSanitizer: (action: any) => any;
  stateSanitizer: (state: any) => any;
} {
  return {
    actionSanitizer: (action) => {
      const trimmed = options.actionSanitizer
        ? options.actionSanitizer(action)
        : action;
      return trimmed === null ? null : redactTokens(trimmed);
    },
    stateSanitizer: (state) => {
      if (!state || typeof state !== 'object' || !('auth' in state)) {
        return state;
      }
      const auth = redactTokens(state.auth);
      return auth === state.auth ? state : { ...state, auth };
    },
  };
}

/** The one set of LogRocket init options, shared by `frontend` and `clients`. */
export function logRocketOptions(release: string): LogRocketOptions {
  const { requestSanitizer, responseSanitizer } = createNetworkSanitizers();
  return {
    release,
    network: { requestSanitizer, responseSanitizer },
    browser: { urlSanitizer: (url) => sanitizeUrl(url) },
  };
}
